import { COMPACT_DESKTOP_QUERY } from "../../styles/compactDesktop";
import React, { lazy, Suspense } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import PageContainer from "../PageContainer";
import OrganisationSetupBanner from "../shared/OrganisationSetupBanner";
import GettingStartedChecklist from "../shared/GettingStartedChecklist";
import DashboardModals, {
  type DashboardModalsProps,
} from "./DashboardModals";
import FeaturedCarousel from "./FeaturedCarousel";
import DraftNoticesToast from "./DraftNoticesToast";
import JoinRequestsToast from "./JoinRequestsToast";
import DidYouKnowCard from "./DidYouKnowCard";
import AdCenterSetupPrompt from "./AdCenterSetupPrompt";
import DashboardChartSkeleton from "./DashboardChartSkeleton";
import StatsPanel from "./StatsPanel";
import DriveHomeCta from "./DriveHomeCta";
import DashboardCalendar from "./DashboardCalendar";
import NoNoticesCard from "./NoNoticesCard";
import { DASHBOARD_TUTORIAL_VIDEOS } from "../../constants/dashboardTutorialVideos";
import type { getDashboardDisplayState } from "../../domain/dashboard/dashboardConditions";
import type { Notice } from "../../services/api/types";

const NoticesLineChart = lazy(() => import("./NoticesLineChart"));
const NoticeTypesPieChart = lazy(() => import("./NoticeTypesPieChart"));

type DashboardDisplayState = ReturnType<typeof getDashboardDisplayState>;

type HeroQuickAction = {
  label: string;
  onClick: () => void;
  variant: string;
};

type DesktopChartQuickAction = {
  label: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  onClick: () => void;
};

type ChecklistItem = {
  label: string;
  done: boolean;
  onClick: () => void;
  videoPath: string;
  videoTitle: string;
};

export type DashboardDesktopHomeProps = {
  schoolName?: string;
  userType?: "admin" | "team";
  fullName?: string;
  orgId?: string;
  notices?: Notice[];
  onCompleteSetup: () => void;
  heroQuickActions: HeroQuickAction[];
  draftNoticesCount: number;
  joinRequestsCount: number;
  handleViewNotices: () => void;
  handleViewJoinRequests: () => void;
  display: DashboardDisplayState;
  isAdCenterEnabled: boolean;
  handleAdCenter: () => void;
  checklistItems: ChecklistItem[];
  handlePlayVideo: (videoPath: string, title: string) => void;
  desktopChartQuickActions: DesktopChartQuickAction[];
  filteredNoticesByDay: Array<{ date: string; count: number }>;
  chartTitle: string;
  noticesByType: Array<{ type: string; count: number }>;
  handleCalendarDateClick: (slotInfo: { start: Date; end: Date }) => void;
  handleCreateNotice: () => void;
  stats: {
    totalUsers: number;
    totalNotices: number;
    upcomingEvents: number;
    activeAdverts: number;
  };
  dashboardModalsProps: DashboardModalsProps;
};

const DashboardDesktopHome: React.FC<DashboardDesktopHomeProps> = ({
  schoolName,
  userType,
  fullName,
  orgId,
  notices = [],
  onCompleteSetup,
  heroQuickActions,
  draftNoticesCount,
  joinRequestsCount,
  handleViewNotices,
  handleViewJoinRequests,
  display,
  isAdCenterEnabled,
  handleAdCenter,
  checklistItems,
  handlePlayVideo,
  desktopChartQuickActions,
  filteredNoticesByDay,
  chartTitle,
  noticesByType,
  handleCalendarDateClick,
  handleCreateNotice,
  stats,
  dashboardModalsProps,
}) => {
  const compact = useMediaQuery(COMPACT_DESKTOP_QUERY);
  return (
  <PageContainer sx={{ mt: compact ? 0 : -1 }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "1fr",
          lg: "1fr",
          "@media (min-width: 1200px)": {
            gridTemplateColumns: "2.8fr 1.1fr",
          },
        },
        gap: compact ? 2 : 4,
        mt: 0,
        mb: 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <OrganisationSetupBanner
          onCompleteSetup={onCompleteSetup}
          sx={{ mb: 2 }}
        />

        <FeaturedCarousel
          compact={compact}
          schoolName={schoolName}
          userType={userType}
          fullName={fullName}
          orgId={orgId}
          quickActions={heroQuickActions}
        />

        <DraftNoticesToast
          compact={compact}
          count={draftNoticesCount}
          onViewDrafts={handleViewNotices}
        />

        <JoinRequestsToast
          compact={compact}
          count={joinRequestsCount}
          onViewRequests={handleViewJoinRequests}
        />

        {display.showNoNoticesCard && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md:
                    display.showAdCenterSetupCard && isAdCenterEnabled
                      ? "1fr 1fr"
                      : "1fr",
                },
                gap: 3,
              }}
            >
              <GettingStartedChecklist
                items={checklistItems}
                onPlayVideo={handlePlayVideo}
                vertical={display.showAdCenterSetupCard && isAdCenterEnabled}
              />

              {display.showAdCenterSetupCard && isAdCenterEnabled && (
                <DidYouKnowCard onSetupAdCenter={handleAdCenter} />
              )}
            </Box>
          </Box>
        )}

        {display.showCharts && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                "@media (min-width: 1200px)": {
                  gridTemplateColumns: "1fr 1fr",
                },
              },
              gap: compact ? 1.5 : 2,
              mt: compact ? 1.5 : 2,
            }}
          >
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: compact ? "none" : "flex",
                flexWrap: "wrap",
                gap: { xs: 1.25, md: 1.55 },
                justifyContent: "center",
                width: "100%",
                mx: "auto",
              }}
            >
              {desktopChartQuickActions.map((action) => (
                <Box
                  key={`desktop-chart-action-${action.label}`}
                  role="button"
                  tabIndex={0}
                  onClick={action.onClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      action.onClick();
                    }
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    minWidth: { xs: 86, md: 108 },
                    py: 0.2,
                    "&:hover": {
                      transform: "translateY(-1px)",
                    },
                    "&:focus-visible": {
                      outline: (t) => `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                      borderRadius: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 44, md: 52 },
                      height: { xs: 44, md: 52 },
                      borderRadius: "50%",
                      bgcolor: action.iconBg,
                      color: action.iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: { xs: 0.5, md: 0.65 },
                      "& svg": {
                        fontSize: { xs: 23, md: 26 },
                      },
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: "0.72rem", md: "0.88rem" },
                      color: "text.primary",
                      lineHeight: 1.2,
                    }}
                  >
                    {action.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Suspense fallback={<DashboardChartSkeleton variant="line" />}>
                <NoticesLineChart
                  compact={compact}
                  data={filteredNoticesByDay}
                  title={chartTitle}
                />
              </Suspense>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Suspense fallback={<DashboardChartSkeleton variant="pie" />}>
                <NoticeTypesPieChart data={noticesByType} compact={compact} />
              </Suspense>
            </Box>
          </Box>
        )}

        {display.showAdCenterSetupCard &&
          isAdCenterEnabled &&
          !display.showNoNoticesCard && (
            <AdCenterSetupPrompt onSetupAdCenter={handleAdCenter} />
          )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: compact ? 1 : 2,
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        <DashboardCalendar
          compact={compact}
          notices={notices}
          onSelectSlot={handleCalendarDateClick}
        />

        {display.showStats && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: compact ? 1 : { xs: 1, md: 1.5 },
            }}
          >
            <DriveHomeCta compact={compact} />
            <StatsPanel
              compact={compact}
              totalUsers={stats.totalUsers}
              totalNotices={stats.totalNotices}
              upcomingEvents={stats.upcomingEvents}
              activeAdverts={stats.activeAdverts}
              showActiveAdverts={isAdCenterEnabled}
            />
          </Box>
        )}

        {display.showNoNoticesCard && (
          <NoNoticesCard
            onCreateNotice={handleCreateNotice}
            onPlayVideo={() =>
              handlePlayVideo(
                DASHBOARD_TUTORIAL_VIDEOS.createNotice.path,
                DASHBOARD_TUTORIAL_VIDEOS.createNotice.title,
              )
            }
          />
        )}
      </Box>
    </Box>

    <DashboardModals {...dashboardModalsProps} />
  </PageContainer>
);
};

export default DashboardDesktopHome;
